import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Autocomplete, Button, FormControl, TextField } from '@mui/material';
import { Col, Row } from 'react-bootstrap';
import { useToast } from '../../../context/Toast';
import { toggleUserStatus } from '../../../services/user/user';
import { useAuth } from '../../../context/AuthContext';
import { LabelNames } from '../../../pages/userRegister/style';
import { ButtonAddGroup, StyledModalFooterAddMemberOnGroup } from '../../../pages/settings/styles';
import { StyledModal } from '../../../pages/settings/modalUser/styles';
import { UserTableModel } from '../../dataTable/userTable';
import { INACTIVATION_REASONS} from '../../../data/inactivationReasons';
import { INACTIVATION_REASON_TRANSLATION_KEYS } from '../../../data/inactivationReasonTranslations';



interface ModalUserInactivationReasonProps {
  show: boolean;
  onClose: () => void;
  userInfo: UserTableModel | null;
  onSuccess?: (updatedUser?: any) => Promise<void> | void;
  onConflict?: () => Promise<void> | void;
}

const normalizeText = (value?: string | null) => (value ?? '').trim();

const sortInactivationReasons = (options: typeof INACTIVATION_REASONS) =>
  [...options].sort((a, b) => {
    const aIsCustom = a.movementTypeLabelPtBr === 'Outro';
    const bIsCustom = b.movementTypeLabelPtBr === 'Outro';

    if (aIsCustom !== bIsCustom) {
      return aIsCustom ? 1 : -1;
    }

    const groupCompare = a.movementTypeLabelPtBr.localeCompare(b.movementTypeLabelPtBr, 'pt-BR', {
      sensitivity: 'base',
    });

    if (groupCompare !== 0) {
      return groupCompare;
    }

    return a.labelPtBr.localeCompare(b.labelPtBr, 'pt-BR', { sensitivity: 'base' });
  });

function ModalUserInactivationReason({
  show,
  onClose,
  userInfo,
  onSuccess,
  onConflict,
}: ModalUserInactivationReasonProps) {
  const { t } = useTranslation('settings');
  const { showToast } = useToast();
  const { user } = useAuth();
  const [showLoading, setShowLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [customReasonText, setCustomReasonText] = useState('');

  const informedRequesterId = user?.user?.id;
  const reasonOptions = useMemo(() => sortInactivationReasons(INACTIVATION_REASONS), []);

  useEffect(() => {
    if (!show) {
      setReason('');
      setCustomReasonText('');
      setShowLoading(false);
      return;
    }

    setReason('');
    setCustomReasonText('');
    setShowLoading(false);
  }, [show, userInfo]);

  if (!show || !userInfo) return null;

  const firstName = (userInfo.first_name || userInfo.rawUser?.first_name || '').trim();
  const lastName = (userInfo.last_name || userInfo.rawUser?.last_name || '').trim();
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || userInfo.email || String(t('inactivation.requesterUnknown'));
  const userEmail = userInfo.email || userInfo.rawUser?.email || '';
  const title = t('inactivation.title', { name: displayName, email: userEmail ? ` (${userEmail})` : '' });
  const confirmLabel = String(t('inactivation.confirm'));
  const actionDescription = String(t('inactivation.description'));
  const isCustomReason = reason === 'CUSTOM';

  const handleClose = () => {
    setReason('');
    setCustomReasonText('');
    setShowLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!userInfo?.id) return;
    if (!informedRequesterId) {
      showToast(t('users.requesterMissing'), 'error', 5000);
      return;
    }

    if (!reason) {
      showToast(t('inactivation.selectReason'), 'warning', 4000);
      return;
    }

    if (isCustomReason && !normalizeText(customReasonText)) {
      showToast(t('inactivation.describeReason'), 'warning', 4000);
      return;
    }

    setShowLoading(true);

    const body: {
      id: string;
      informedRequesterId: string;
      currentUserStatus: number;
      reasonCode: string;
      customReasonText?: string;
    } = {
      id: userInfo.id,
      informedRequesterId,
      currentUserStatus: userInfo.status,
      reasonCode: reason,
    };

    if (isCustomReason) {
      body.customReasonText = normalizeText(customReasonText);
    }

    try {
      const response = await toggleUserStatus(body);

      showToast(response?.message || t('inactivation.updated'), 'success', 3000);

      try {
        await onSuccess?.(response?.user);
      } catch (callbackError) {
        console.error(callbackError);
      }

      handleClose();
    } catch (error: any) {
      console.error(error);

      if (error?.response?.status === 409) {
        showToast(
          t('inactivation.conflict'),
          'warning',
          5000
        );

        try {
          await onConflict?.();
        } catch (conflictCallbackError) {
          console.error(conflictCallbackError);
        }

        handleClose();
        return;
      }

      if (error?.response?.status === 404) {
        showToast(t('users.notFound'), 'error', 5000);
        return;
      }

      if (error?.response?.status === 400) {
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errorTitle ||
          'Não foi possível validar a solicitação.';

        showToast(backendMessage, 'error', 6000);
        return;
      }

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errorTitle ||
        'Erro ao processar a ação de inativação.';

      showToast(backendMessage, 'error', 8000);
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <StyledModal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      dialogClassName="user-inactivation-modal-dialog"
      contentClassName="user-inactivation-modal-content"
    >
      <Modal.Header>
        <Modal.Title style={{ color: '#3C4043' }}>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          <Col xs={12}>
            <LabelNames style={{ color: '#3C4043' }}>{actionDescription}</LabelNames>
            <FormControl
              fullWidth
              sx={{
                mt: 2,
                background: '#F1F1F1',
                borderRadius: '12px',
                border: '1px solid #D1D5DB',
              }}
            >
              <Autocomplete
                options={reasonOptions}
                groupBy={(option) =>
                  String(t(`inactivation.movementTypes.${option.movementType}`))
                }
                getOptionLabel={(option) =>
                  String(t(INACTIVATION_REASON_TRANSLATION_KEYS[option.reasonCode]))
                }
                value={reasonOptions.find((option) => option.reasonCode === reason) ?? null}
                onChange={(_, nextOption) => {
                  const nextReason = nextOption?.reasonCode ?? '';
                  setReason(nextReason);

                  if (nextReason !== 'CUSTOM') {
                    setCustomReasonText('');
                  }
                }}
                isOptionEqualToValue={(option, value) => option.reasonCode === value.reasonCode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={String(t('inactivation.placeholder'))}
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      sx: {
                        minHeight: '56px',
                        alignItems: 'center',
                        px: 1.5,
                      },
                    }}
                  />
                )}
                noOptionsText="Nenhum motivo encontrado"
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    borderRadius: '12px',
                    background: '#F1F1F1',
                    minHeight: '56px',
                    alignItems: 'center',
                    px: 1.5,
                  },
                  '& .MuiAutocomplete-input': {
                    py: 0,
                  },
                  '& .MuiAutocomplete-listbox': {
                    maxHeight: 280,
                    overflowY: 'auto',
                  },
                }}
              />
            </FormControl>

            {isCustomReason && (
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={customReasonText}
                onChange={(event) => setCustomReasonText(event.target.value)}
                placeholder={String(t('inactivation.descriptionPlaceholder'))}
                label={String(t('inactivation.customReason'))}
                sx={{
                  mt: 2,
                  background: '#F1F1F1',
                  borderRadius: '12px',
                }}
              />
            )}
          </Col>
        </Row>
      </Modal.Body>

      <StyledModalFooterAddMemberOnGroup>
        <span style={{ color: '#A5A5AA', fontSize: '14px', textAlign: 'left' }}>
              {t('inactivation.hint', { action: confirmLabel })}
        </span>

        <ButtonAddGroup>
          <Button
            sx={{
              textTransform: 'capitalize',
              padding: '0.5em 1.5em',
              borderRadius: '2em',
              fontSize: '1em',
              fontWeight: 500,
              border: '1px solid #a0a0a0',
            }}
            variant="text"
            onClick={handleClose}
            disabled={showLoading}
          >
              {t('inactivation.close')}
          </Button>
          <Button
            sx={{
              textTransform: 'capitalize',
              background: '#2491FF',
              borderRadius: '2em',
              fontSize: '1em',
              fontWeight: 'semibold ',
              padding: '0.5em 2em',
            }}
            variant="contained"
            onClick={handleSubmit}
            disabled={
              showLoading ||
              reason.trim().length === 0 ||
              (isCustomReason && normalizeText(customReasonText).length === 0)
            }
          >
            {showLoading ? t('inactivation.confirming') : confirmLabel}
          </Button>
        </ButtonAddGroup>
      </StyledModalFooterAddMemberOnGroup>
    </StyledModal>
  );
}

export default ModalUserInactivationReason;
